import { useEffect, useRef } from 'react'
import { track } from '../utils/analytics'

const IDLE_TIMEOUT_MS = 30 * 60 * 1000

const IDLE_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const

export function useStageTracking(stageName: string): void {
  const enterTimeRef = useRef(Date.now())
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    enterTimeRef.current = Date.now()
    track('consent.stage_entered', { stage: stageName })

    return () => {
      track('consent.stage_time', {
        stage: stageName,
        duration_ms: Date.now() - enterTimeRef.current,
      })
    }
  }, [stageName])

  // beforeunload covers tab close / external navigation; SPA back-navigation is tracked at call sites.
  useEffect(() => {
    const onBeforeUnload = () => {
      track('consent.drop_off', {
        stage: stageName,
        duration_ms: Date.now() - enterTimeRef.current,
      })
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [stageName])

  useEffect(() => {
    const resetTimer = () => {
      if (idleTimerRef.current !== null) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => {
        track('consent.session_timeout', {
          stage: stageName,
          duration_ms: Date.now() - enterTimeRef.current,
        })
      }, IDLE_TIMEOUT_MS)
    }

    IDLE_EVENTS.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }))
    resetTimer()

    return () => {
      if (idleTimerRef.current !== null) clearTimeout(idleTimerRef.current)
      IDLE_EVENTS.forEach((e) => window.removeEventListener(e, resetTimer))
    }
  }, [stageName])
}
